// TEST: Hot reloading test - UPDATED! If you see this change, hot reloading is working!
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import Container from './components/layout/Container';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { InteractionsProvider } from './contexts/InteractionsContext';
import { SessionDataProvider } from './contexts/SessionDataContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PendingCompanyInviteGate from './components/auth/PendingCompanyInviteGate';
import ErrorBoundary from './components/shared/ErrorBoundary';
import theme from './theme';

// Lazy load components
const Collections = lazy(() => import('./components/collections'));
const Drafts = lazy(() => import('./components/drafts'));
const PromptBuilder = lazy(() => import('./components/prompt-builder'));
const Assistant = lazy(() => import('./components/freestyle'));
const PromptLibrary = lazy(() => import('./components/prompt-library'));
const CommunityLibrary = lazy(() => import('./components/community-library'));
const ContextRepository = lazy(() => import('./components/context-repository'));
const Integrations = lazy(() => import('./components/integrations'));

// Lazy load auth components
const AuthPage = lazy(() => import('./components/auth/AuthPage'));
const UserProfile = lazy(() => import('./components/auth/UserProfile'));
const AuthCallback = lazy(() => import('./components/auth/AuthCallback'));
const McpAuthorize = lazy(() => import('./components/auth/McpAuthorize'));

// Lazy load shared components
const SharedPrompt = lazy(() => import('./components/shared/SharedPrompt'));
const Help = lazy(() => import('./components/shared/Help'));
const SearchPage = lazy(() => import('./components/shared/SearchPage'));


// Lazy load admin components
const UsersAndRoles = lazy(() => import('./components/admin/UsersAndRoles'));
const AccountSettings = lazy(() => import('./components/admin/AccountSettings'));
const Analytics = lazy(() => import('./components/admin/Analytics'));
const Billing = lazy(() => import('./components/admin/Billing'));

// Loading component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Community capability URLs are intentionally public for durable sharing. Signed-in
// users keep the native Community detail experience; public visitors see the
// read-only shared capability preview instead of being sent to sign-in.
const CommunityCapabilityRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <SharedPrompt />;
  }

  return (
    <ProtectedRoute>
      <Container>
        <CommunityLibrary />
      </Container>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SessionDataProvider>
            <InteractionsProvider>
              <Router future={{ v7_startTransition: true }}>
                <PendingCompanyInviteGate />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
              {/* Authentication Routes */}
              <Route 
                path="/auth/login" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/auth/register" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/auth/founders"
                element={<ProtectedRoute requireAuth={false}><AuthPage /></ProtectedRoute>}
              />
              <Route 
                path="/auth/forgot-password" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/auth/verify-email" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/auth/social-signup" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/auth/callback"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/auth/mcp/authorize"
                element={
                  <ProtectedRoute>
                    <McpAuthorize />
                  </ProtectedRoute>
                }
              />

              {/* Shared Routes (no auth required) */}
              <Route 
                path="/shared/:shareToken" 
                element={<SharedPrompt />} 
              />
              <Route
                path="/public/capabilities/:capabilityId"
                element={<SharedPrompt />}
              />

              {/* Protected Main Routes */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <PromptLibrary />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/library" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <PromptLibrary />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/library/:promptId" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <PromptLibrary />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/community" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <CommunityLibrary />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/community/category/:categoryName" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <CommunityLibrary />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/community/:promptId"
                element={<CommunityCapabilityRoute />}
              />
              <Route 
                path="/collections" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Collections />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/collections/:collectionId" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Collections />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/drafts" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Drafts />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/contexts/:contextId" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <ContextRepository />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/contexts" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <ContextRepository />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/prompt-builder" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <PromptBuilder />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/assistant" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Assistant />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <Container>
                      <Integrations />
                    </Container>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <UserProfile />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/help" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Help />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/search" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <SearchPage />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Admin Routes */}
              <Route 
                path="/admin/users" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <UsersAndRoles />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/settings" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <AccountSettings />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/analytics" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Analytics />
                    </Container>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/billing" 
                element={
                  <ProtectedRoute>
                    <Container>
                      <Billing />
                    </Container>
                  </ProtectedRoute>
                } 
              />
                  </Routes>
                </Suspense>
              </Router>
            </InteractionsProvider>
          </SessionDataProvider>
        </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 
